/**
 * Client-facing Tax Provisioning summary card — simple version.
 * Shows the latest PUBLISHED tax estimate + savings targets.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PiggyBank, CheckCircle2 } from "lucide-react";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const nz = (v) => (v == null ? "—" : NZD.format(v));

export default function PortalTaxProvisioningCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/tax-provisioning/portal/latest")
            .then(({ data }) => setData(data))
            .catch(() => setData({ snapshot: null }))
            .finally(() => setLoading(false));
    }, []);
    if (loading) return null;
    if (!data?.snapshot) return null;
    const snap = data.snapshot;
    const c = snap.calc || {};
    const funded = c.status === "fully_funded";

    return (
        <Card className="p-5" data-testid="portal-tp-card">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">Your estimated tax</div>
                    <h2 className="text-[17px] font-semibold text-[#0F1115]">As at {c.as_at}</h2>
                </div>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11.5px] ${funded ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-orange-50 border-orange-200 text-orange-800"}`}>
                    {funded ? <CheckCircle2 className="h-3.5 w-3.5" /> : <PiggyBank className="h-3.5 w-3.5" />}
                    {funded ? "Fully funded" : "Save now"}
                </span>
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-[13px]">
                <Cell label="Total taxable income" value={nz(c.total_taxable_income ?? c.net_profit)} sub={c.method_note} />
                <Cell label="Income tax" value={nz(c.gross_tax)} />
                <Cell
                    label={`ACC earner levy${c.acc_levy?.capped ? " · capped" : ""}`}
                    value={c.acc_levy?.applies ? nz(c.acc_levy.levy) : "n/a"}
                    sub={c.acc_levy?.applies ? `1.75% on ${nz(c.acc_levy.taxed_on)}` : "Not applicable"}
                />
                <Cell label="Total to fund" value={nz(c.total_to_fund)} />
                <Cell label="Tax already paid" value={nz(c.tax_already_paid)} />
                <Cell label="Still to save" value={nz(c.net_exposure)} highlight={c.net_exposure > 0} />
            </div>

            {(c.income_sources || []).length > 0 && (
                <div className="mt-4 rounded-md border border-[#EEEDE7] p-3 text-[12.5px]" data-testid="portal-tp-income">
                    <div className="font-semibold text-[#0F1115] mb-1.5">Income breakdown</div>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                                <th className="text-left font-medium py-1">Source</th>
                                <th className="text-right font-medium py-1">Amount</th>
                                <th className="text-right font-medium py-1">Tax withheld</th>
                            </tr>
                        </thead>
                        <tbody>
                            {c.income_sources.map((s) => (
                                <tr key={s.key} className="border-t border-[#EEEDE7]">
                                    <td className="py-1.5">{s.label}</td>
                                    <td className="py-1.5 text-right tabular-nums">{nz(s.amount)}</td>
                                    <td className="py-1.5 text-right tabular-nums text-[#5E636E]">{s.withheld ? nz(s.withheld) : "—"}</td>
                                </tr>
                            ))}
                            <tr className="border-t border-[#EEEDE7]">
                                <td className="py-1.5 font-semibold">Total taxable income</td>
                                <td className="py-1.5 text-right font-semibold tabular-nums">{nz(c.total_taxable_income)}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {!funded && (
                <div className="mt-4 rounded-md bg-[#F5F5F0] p-3 text-[13px] text-[#0F1115]">
                    To have <b>{nz(c.net_exposure)}</b> set aside by <b>{c.fy_end}</b>, save about{" "}
                    <b>{nz(c.recommended?.weekly)}</b>/week · <b>{nz(c.recommended?.fortnightly)}</b>/fortnight · <b>{nz(c.recommended?.monthly)}</b>/month.
                </div>
            )}

            {funded && (
                <div className="mt-4 rounded-md bg-emerald-50 p-3 text-[13px] text-emerald-900">
                    You're on track — tax already paid covers the estimated liability.
                </div>
            )}

            {c.bracket_breakdown && (c.bracket_breakdown.length > 0 || c.acc_levy?.applies) && (
                <div className="mt-4 rounded-md border border-[#EEEDE7] p-3 text-[12.5px]" data-testid="portal-tp-brackets">
                    <div className="flex items-baseline justify-between mb-1.5">
                        <div className="font-semibold text-[#0F1115]">Bracket breakdown</div>
                        <div className="text-[11px] text-[#5E636E]">Effective rate {Number(c.effective_rate || 0)}%</div>
                    </div>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                                <th className="text-left font-medium py-1">Band</th>
                                <th className="text-right font-medium py-1">Rate</th>
                                <th className="text-right font-medium py-1">Taxed on</th>
                                <th className="text-right font-medium py-1">Tax</th>
                            </tr>
                        </thead>
                        <tbody>
                            {c.bracket_breakdown.map((b, i) => {
                                const n = Math.round(Number(b.rate) * 10000) / 100;
                                const s = n.toString();
                                const pct = `${s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s}%`;
                                return (
                                    <tr key={i} className="border-t border-[#EEEDE7]">
                                        <td className="py-1.5">{b.label}</td>
                                        <td className="py-1.5 text-right">{pct}</td>
                                        <td className="py-1.5 text-right tabular-nums">{nz(b.taxed_on)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{nz(b.tax)}</td>
                                    </tr>
                                );
                            })}
                            {c.bracket_breakdown.length > 0 && (
                                <tr className="border-t border-[#EEEDE7]">
                                    <td className="py-1.5 text-[#5E636E]" colSpan={3}>Income tax subtotal</td>
                                    <td className="py-1.5 text-right tabular-nums text-[#5E636E]">{nz(c.gross_tax)}</td>
                                </tr>
                            )}
                            {c.acc_levy?.applies && (
                                <tr className="border-t border-[#EEEDE7]">
                                    <td className="py-1.5">ACC earner levy{c.acc_levy.capped ? " (capped)" : ""}</td>
                                    <td className="py-1.5 text-right">1.75%</td>
                                    <td className="py-1.5 text-right tabular-nums">{nz(c.acc_levy.taxed_on)}</td>
                                    <td className="py-1.5 text-right tabular-nums">{nz(c.acc_levy.levy)}</td>
                                </tr>
                            )}
                            <tr className="border-t border-[#EEEDE7]">
                                <td className="py-1.5 font-semibold" colSpan={3}>Total to fund</td>
                                <td className="py-1.5 text-right font-semibold tabular-nums">{nz(c.total_to_fund)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <p className="mt-4 text-[11px] text-[#5E636E] leading-relaxed">{data.disclaimer}</p>
            {c.acc_levy?.applies && (
                <p className="mt-2 text-[11px] text-[#5E636E] leading-relaxed">
                    <b>Note on ACC:</b> ACC earner levy shown is an estimate for savings planning only. Your actual ACC invoice may differ.
                </p>
            )}
        </Card>
    );
}

function Cell({ label, value, sub, highlight }) {
    return (
        <div className={`rounded-md border p-2 ${highlight ? "border-red-200 bg-red-50" : "border-[#EEEDE7] bg-white"}`}>
            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">{label}</div>
            <div className="mt-1 text-[15px] font-semibold text-[#0F1115] tabular-nums">{value}</div>
            {sub && <div className="text-[11px] text-[#5E636E] mt-0.5 truncate">{sub}</div>}
        </div>
    );
}
